import React, { useEffect, useState } from 'react';
import AIChatWidget from './components/AI/AIChatWidget';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { createTheme, ThemeProvider as MuiThemeProvider } from "@mui/material/styles";
import { UserProvider } from './components/UserContext';
import Home from './components/Home';
import Users from './components/Users';
import Members from './components/ClubMembers';
import Login from './login/Login';
import SSOCallback from './login/SSOCallback';
import ResetPassword from './login/ResetPassword';
import RequestAccess from './login/RequestAccess';
import Dashboard from './components/Dashboard';
import Violations from './components/Violations';
import ViolationsDetails from './components/ViolationsDetails';
import Workorders from './components/WorkOrders';
import WorkOrdersDetails from './components/WorkOrdersDetails';
import NewWorkOrder from './components/NewWorkOrder';
import FileManager from './components/FileManager';
import ClubMembers from './components/Memebers';
import MemberProfilePage from './components/MemberProfilePage';
import MembersPage from './components/MemberPage';
import MembersPageHeritage from './components/MemberPageheritage';
import ArchitecturalRequest from './components/ArchitecturalRequest';
import ArchitecturalRequestDetails from './components/ArchitecturalRequestDetails';
import Properties from './components/Properties';
import SystemPreferences from './components/SystemPreferences';
import Reports from './components/Reports';
import Profile from './components/MemberProfilePage';
import MemberViolationDetails from './components/OwnerDashboard/MemberViolationDetails';
import MemberWorkordersDetails from './components/OwnerDashboard/MemberWorkordersDetails';
import MemberWorkordersDetailsHeritage from './components/OwnerDashboard/MemberWorkordersDetailsheritage';
import MemberWorkordersPageHeritage from './components/OwnerDashboard/WorkOrdersheritage';
import MemberArchitecturalRequests from './components/OwnerDashboard/MemberArchitecturalRequestDetails';
import UserProfile from './components/Userprofile';
import Integrations from './components/Integrations/Integrations';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { ThemeProvider } from "./utils/ThemeContext";
import { ClientConfigProvider, useClientConfig } from "./utils/ClientConfigContext";
import ThemeSettings from "./components/ThemeSettings";
import NotFound from './components/NotFound';
import ProtectedRoute from './components/ProtectedRoute';
import AccessDenied from './components/AccessDenied';
import ErrorBoundary from './components/ErrorBoundary';
import GateSecurity from './components/GateSecurity';
import Voting from './components/Voting';
import VotePage from './components/Voting/VotePage';
import Marina from './components/Marina';



// Always-light MUI theme for public pages (login, reset-password, request-access)
const PUBLIC_LIGHT_THEME = createTheme({
  palette: { mode: "light" },
  typography: { fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif" },
});

/**
 * Wraps public pages so dark mode never affects them.
 * - Overrides the MUI theme to always be light
 * - Removes data-theme="dark" from <html> while mounted, restores on unmount
 */
const ForceLightPage = ({ children }) => {
  useEffect(() => {
    const html = document.documentElement;
    const prev = html.getAttribute("data-theme");
    html.setAttribute("data-theme", "light");
    return () => {
      if (prev) html.setAttribute("data-theme", prev);
      else html.removeAttribute("data-theme");
    };
  }, []);

  return (
    <MuiThemeProvider theme={PUBLIC_LIGHT_THEME}>
      {children}
    </MuiThemeProvider>
  );
};

// Separate component so it can read ClientConfigContext (hooks can't be called in App directly)
function AppRoutes() {
  const { isHeritage } = useClientConfig();

  // Swap owner-dashboard components based on terminology setting
  const OwnerMembersPage         = isHeritage ? MembersPageHeritage         : MembersPage;
  const OwnerWorkorderDetail     = isHeritage ? MemberWorkordersDetailsHeritage : MemberWorkordersDetails;

  return (
    <Routes>
      <Route path="/" element={<Navigate replace to="/login" />} />
      <Route path="/login" element={<ForceLightPage><Login /></ForceLightPage>} />
      <Route path="/sso-callback" element={<ForceLightPage><SSOCallback /></ForceLightPage>} />
      <Route path="/reset-password" element={<ForceLightPage><ResetPassword /></ForceLightPage>} />
      <Route path="/request-access" element={<ForceLightPage><RequestAccess /></ForceLightPage>} />
      <Route path="/vote/:electionId" element={<VotePage />} />

      <Route path="/home" element={<Home />}>
        <Route
          index
          element={
            Number(localStorage.getItem("roleId")) === 3
              ? <Navigate to="/home/memberspage" replace />
              : <ProtectedRoute element={<ErrorBoundary><Dashboard /></ErrorBoundary>} requiredPermission="view_dashboard" />
          }
        />
        <Route path="users" element={<ProtectedRoute element={<ErrorBoundary><Users /></ErrorBoundary>} requiredPermission="view_users" />} />

        <Route path="workorders" element={<ProtectedRoute element={<ErrorBoundary><Workorders /></ErrorBoundary>} requiredPermission="view_work_orders" moduleId={1} />}>
          <Route path="new" element={<ErrorBoundary><NewWorkOrder /></ErrorBoundary>} />
          <Route path=":id" element={<ErrorBoundary><WorkOrdersDetails /></ErrorBoundary>} />
        </Route>

        <Route path="members" element={<ProtectedRoute element={<ErrorBoundary><Members /></ErrorBoundary>} requiredPermission="view_members" />} />
        <Route path="properties" element={<ProtectedRoute element={<ErrorBoundary><Properties /></ErrorBoundary>} requiredPermission="view_properties" />}>
          <Route path=":id" element={<ErrorBoundary><Profile /></ErrorBoundary>} />
        </Route>

        <Route path="architecturalrequest" element={<ProtectedRoute element={<ErrorBoundary><ArchitecturalRequest /></ErrorBoundary>} requiredPermission="view_architectural_requests" moduleId={3} />}>
          <Route path=":id" element={<ErrorBoundary><ArchitecturalRequestDetails /></ErrorBoundary>} />
        </Route>

        <Route path="GateSecurity" element={<ProtectedRoute element={<ErrorBoundary><GateSecurity /></ErrorBoundary>} requiredPermission="view_gate" />} />
        <Route path="Voting" element={<ProtectedRoute element={<ErrorBoundary><Voting /></ErrorBoundary>} requiredPermission="view_violations" />} />

        <Route path="filemanager" element={<ProtectedRoute element={<ErrorBoundary><FileManager /></ErrorBoundary>} requiredPermission="view_file_manager" />} />
        <Route path="reports" element={<ProtectedRoute element={<ErrorBoundary><Reports /></ErrorBoundary>} requiredPermission="view_reports" />} />
        <Route path="integrations" element={<ProtectedRoute element={<ErrorBoundary><Integrations /></ErrorBoundary>} requiredPermission="view_billing" />} />

        <Route path="violations" element={<ProtectedRoute element={<ErrorBoundary><Violations /></ErrorBoundary>} requiredPermission="view_violations" moduleId={2} />}>
          <Route path=":id" element={<ErrorBoundary><ViolationsDetails /></ErrorBoundary>} />
        </Route>

        {/* Owner dashboard. swaps to heritage version based on ClientConfig */}
        <Route path="memberspage" element={<ProtectedRoute element={<ErrorBoundary><OwnerMembersPage /></ErrorBoundary>} requiredPermission="view_owners" />} />
        <Route path="memberspage/violation/:id" element={<ErrorBoundary><MemberViolationDetails /></ErrorBoundary>} />
        <Route path="memberspage/workorder/:id" element={<ErrorBoundary><OwnerWorkorderDetail /></ErrorBoundary>} />
        <Route path="memberspage/ArchitecturalRequests/:id" element={<ErrorBoundary><MemberArchitecturalRequests /></ErrorBoundary>} />

        <Route path="clubmembers" element={<ProtectedRoute element={<ErrorBoundary><ClubMembers /></ErrorBoundary>} requiredPermission="view_members" />} />
        <Route path="clubmembers/:ClubMemberCode" element={<ErrorBoundary><MemberProfilePage /></ErrorBoundary>} />
        <Route path="marina" element={<ProtectedRoute element={<ErrorBoundary><Marina /></ErrorBoundary>} requiredPermission="marina.view" moduleId={5} />} />
        <Route path="system-preferences" element={<ProtectedRoute element={<ErrorBoundary><SystemPreferences /></ErrorBoundary>} requiredPermission="manage_settings" />} />
        <Route path="user-profile" element={<ErrorBoundary><UserProfile /></ErrorBoundary>} />
        <Route path="theme-settings" element={<ThemeSettings />} />
      </Route>

      <Route path="*" element={<NotFound />} />
      <Route path="/access-denied" element={<AccessDenied />} />
    </Routes>
  );
}

function App() {
  const [googleClientId, setGoogleClientId] = useState('');

  useEffect(() => {
    fetch('/api/auth/sso/providers')
      .then((r) => r.json())
      .then((data) => {
        if (data?.google?.googleClientId) setGoogleClientId(data.google.googleClientId);
      })
      .catch(() => {});
  }, []);

  return (
    <GoogleOAuthProvider clientId={googleClientId || 'placeholder'}>
    <ThemeProvider>
    <ClientConfigProvider>
    <UserProvider>
      <Router>
        <AppRoutes />
        {/* AI Chat widget. globally available, renders only for authorized users */}
        <AIChatWidget />
      </Router>
    </UserProvider>
    </ClientConfigProvider>
    </ThemeProvider>
    </GoogleOAuthProvider>
  );
}

export default App;
